import React, { useState, useEffect, useRef } from "react";
import { entities, uploadFile, openUploadInNewTab } from "@/api/apiClient";
import { processFileWithAI } from "@/lib/extractWithRetry";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Upload, X, Plus, Loader2, Clock, Pencil } from "lucide-react";
import { toast } from "sonner";

function formatarTempo(segundos) {
  const m = Math.floor(segundos / 60);
  const s = segundos % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export default function AtestadoForm({ atestado, onClose }) {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    tipos: atestado?.tipos || [],
    instituicao: atestado?.instituicao || "",
    resumo_executivo: atestado?.resumo_executivo || "",
    competencias_tecnicas: atestado?.competencias_tecnicas || [],
    data_emissao: atestado?.data_emissao || "",
    data_inicio: atestado?.data_inicio || "",
    data_fim: atestado?.data_fim || "",
    valor_contrato: atestado?.valor_contrato || "",
    observacoes: atestado?.observacoes || "",
    anexo_url: atestado?.anexo_url || "",
    anexo_nome: atestado?.anexo_nome || "",
    anexos_contrato: atestado?.anexos_contrato || []
  });

  const [novoTipo, setNovoTipo] = useState("");
  const [novaCompetencia, setNovaCompetencia] = useState("");
  const [tiposDisponiveis, setTiposDisponiveis] = useState(
    atestado?.tipos?.length ? [...atestado.tipos] : []
  );
  const [editingTipo, setEditingTipo] = useState(null);
  const [editingTipoValor, setEditingTipoValor] = useState("");
  const [uploading, setUploading] = useState(false);
  const [processando, setProcessando] = useState(false);
  const [tempoDecorrido, setTempoDecorrido] = useState(0);
  const [uploadingAnexoContrato, setUploadingAnexoContrato] = useState(false);
  const [novoAnexoDescricao, setNovoAnexoDescricao] = useState("");
  const timerRef = useRef(null);
  const inicioRef = useRef(null);

  useEffect(() => {
    if (!processando && !uploading) return;
    inicioRef.current = Date.now();
    setTempoDecorrido(0);
    timerRef.current = setInterval(() => {
      setTempoDecorrido(Math.floor((Date.now() - inicioRef.current) / 1000));
    }, 1000);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [processando, uploading]);

  const saveMutation = useMutation({
    mutationFn: async (data) => {
      if (atestado) {
        return entities.AtestadoTecnico.update(atestado.id, data);
      }
      return entities.AtestadoTecnico.create(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['atestados-tecnicos'] });
      toast.success(atestado ? "Atestado atualizado" : "Atestado criado");
      onClose();
    }
  });

  const handleUploadAnexo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setProcessando(true);
    try {
      const schema = {
        type: "object",
        properties: {
          instituicao: {
            type: "string",
            description: "CRÍTICO - Nome completo da instituição/órgão que emitiu o atestado. Busque em: cabeçalho, rodapé, CONTRATANTE, ÓRGÃO, EMITIDO POR, EMISSOR, prestado a, prestado para.",
          },
          resumo_executivo: {
            type: "string",
            description: "CRÍTICO - Resumo do objeto/serviço prestado. Busque em: OBJETO, ESPECIFICAÇÃO, DESCRIÇÃO DOS SERVIÇOS, escopo do contrato. Extraia o texto completo que descreve o que foi prestado.",
          },
          tipos: {
            type: "array",
            items: { type: "string" },
            description: "Tipos de serviço prestado. Exemplos: Desenvolvimento, Infraestrutura, Sustentação, Suporte N1/N2/N3. Busque no texto por menções a esses tipos.",
          },
          competencias_tecnicas: {
            type: "array",
            items: { type: "string" },
            description: "Array com CADA tecnologia/linguagem/framework em item SEPARADO. Ex: ['Java', 'React', 'PostgreSQL']. NÃO retorne string única. Busque: linguagens, frameworks, bancos de dados, ferramentas, cloud.",
          },
          data_emissao: {
            type: "string",
            description: "Data de emissão do atestado. Formato YYYY-MM-DD. Busque por: data de emissão, emitido em, data.",
          },
          data_inicio: {
            type: "string",
            description: "Data de início do período. Formato YYYY-MM-DD. Busque por: vigência, período de, início em, de ___ a ___.",
          },
          data_fim: {
            type: "string",
            description: "Data de término do período. Formato YYYY-MM-DD. Busque por: vigência até, término em, até ___.",
          },
          valor_contrato: {
            type: "number",
            description: "Valor numérico do contrato se mencionado. Apenas número, sem R$.",
          },
        },
      };

      const prompt = `Você é um especialista em análise de atestados de capacidade técnica e declarações de serviços prestados.

TAREFA: Extraia TODOS os dados solicitados do atestado anexado.

═══════════════════════════════════════════════════════════════
CAMPOS OBRIGATÓRIOS - BUSQUE EM TODO O DOCUMENTO:
═══════════════════════════════════════════════════════════════

1. INSTITUIÇÃO (CRÍTICO): Nome do órgão/empresa que emitiu. Busque em: cabeçalho, rodapé, "CONTRATANTE", "ÓRGÃO", "EMITIDO POR", "prestado a", "prestado para", "contratante".

2. RESUMO EXECUTIVO (CRÍTICO): Descrição do serviço/objeto prestado. Busque em: "OBJETO", "ESPECIFICAÇÃO", "DESCRIÇÃO DOS SERVIÇOS", "escopo", "objeto do contrato". Extraia o texto completo.

3. TIPOS DE SERVIÇO: Array com tipos mencionados. Exemplos: Desenvolvimento, Infraestrutura, Sustentação, Suporte N1 N2 N3, Central de Serviço. Se o texto mencionar "desenvolvimento de sistemas", inclua "Desenvolvimento".

4. COMPETÊNCIAS TÉCNICAS: Array com CADA tecnologia em item separado. Ex: ["Java", "React", "PostgreSQL"]. Busque linguagens, frameworks, bancos, ferramentas. Cada tecnologia = 1 elemento.

5. DATAS (data_emissao, data_inicio, data_fim): Busque por vigência, período, datas. Formato YYYY-MM-DD. "01/01/2024" → 2024-01-01.

6. VALOR_CONTRATO: Valor numérico se mencionado. Apenas número.

Retorne um objeto JSON com TODOS os campos. Use os nomes das chaves EXATAMENTE: instituicao, resumo_executivo, tipos, competencias_tecnicas, data_emissao, data_inicio, data_fim, valor_contrato.`;

      // Usa mesma lógica de Processar Edital: retry no upload, retry na extração, OCR se vazio
      const { file_url, output: rawOutput } = await processFileWithAI(file, {
        prompt,
        schema,
        keyFieldsForEmptyCheck: ["instituicao", "resumo_executivo"],
        criticalFieldsForOcrRetry: ["instituicao", "resumo_executivo", "data_inicio"],
        document_type: "atestado",
        ocr_page_limit: 10,
        tryOcrIfEmpty: true,
        onOcrFallback: () => toast.info("Tentando com OCR (PDF escaneado)..."),
      });

      if (rawOutput && typeof rawOutput === "object") {
        const keyMap = {
          instituicao: "instituicao",
          resumoExecutivo: "resumo_executivo",
          dataEmissao: "data_emissao",
          dataInicio: "data_inicio",
          dataFim: "data_fim",
          valorContrato: "valor_contrato",
        };
        const output = { ...rawOutput };
        Object.entries(keyMap).forEach(([alt, canonical]) => {
          if (output[alt] != null && (output[canonical] == null || output[canonical] === "")) {
            output[canonical] = output[alt];
          }
        });

        const norm = (v) => (v != null && v !== "" ? String(v).trim() : "");
        const normData = (v) => {
          if (v == null || v === "") return "";
          const s = String(v).trim();
          const m = s.match(/(\d{4})-(\d{2})-(\d{2})/) || s.match(/(\d{2})[/-](\d{2})[/-](\d{4})/);
          if (m) {
            if (m[1].length === 4) return s;
            return `${m[3]}-${m[2]}-${m[1]}`;
          }
          return s;
        };

        setFormData((prev) => {
          const novoAnexoCapacidade = {
            descricao: "Atestado de Capacidade Técnica",
            anexo_url: file_url,
            anexo_nome: file.name,
          };
          const jaTemEsteAnexo = prev.anexos_contrato.some(
            (a) => a.anexo_url === file_url || (a.descricao === "Atestado de Capacidade Técnica" && a.anexo_nome === file.name)
          );
          const anexosContrato = jaTemEsteAnexo
            ? prev.anexos_contrato
            : [...prev.anexos_contrato, novoAnexoCapacidade];

          return {
            ...prev,
            instituicao: norm(output.instituicao) || prev.instituicao,
            resumo_executivo: norm(output.resumo_executivo) || prev.resumo_executivo,
            tipos: Array.isArray(output.tipos) ? output.tipos.filter(Boolean) : prev.tipos,
            competencias_tecnicas: (() => {
              const c = output.competencias_tecnicas ?? output.competenciasTecnicas;
              if (Array.isArray(c)) return c.filter(Boolean).map((x) => String(x).trim());
              if (typeof c === "string" && c.trim())
                return c.split(/[,;]/).map((x) => x.trim()).filter(Boolean);
              return prev.competencias_tecnicas;
            })(),
            data_emissao: normData(output.data_emissao) || prev.data_emissao,
            data_inicio: normData(output.data_inicio) || prev.data_inicio,
            data_fim: normData(output.data_fim) || prev.data_fim,
            valor_contrato: output.valor_contrato ?? prev.valor_contrato,
            anexo_url: file_url,
            anexo_nome: file.name,
            anexos_contrato: anexosContrato,
          };
        });
        const extractedTipos = Array.isArray(output.tipos) ? output.tipos.filter(Boolean) : [];
        if (extractedTipos.length > 0) {
          setTiposDisponiveis(prev => {
            const combined = [...prev];
            extractedTipos.forEach(t => {
              if (!combined.includes(t)) combined.push(t);
            });
            return combined;
          });
        }
        toast.success("Dados extraídos do atestado com sucesso! O PDF foi adicionado como anexo.");
      }
    } catch (error) {
      console.error("Erro ao processar atestado:", error);
      const msg = (error?.message || error?.data?.message || "").toLowerCase();
      const isPdfEscaneado = msg.includes("extrair texto") || msg.includes("não foi possível") || msg.includes("texto extraível");
      toast.error(
        isPdfEscaneado
          ? "O documento parece ser uma imagem escaneada. Tente um PDF com texto selecionável ou preencha manualmente."
          : "Erro ao processar atestado. Tente novamente."
      );
    } finally {
      setUploading(false);
      setProcessando(false);
    }
  };

  const handleToggleTipo = (tipo) => {
    setFormData(prev => ({
      ...prev,
      tipos: prev.tipos.includes(tipo)
        ? prev.tipos.filter(t => t !== tipo)
        : [...prev.tipos, tipo]
    }));
  };

  const handleAdicionarTipo = () => {
    if (novoTipo.trim() && !tiposDisponiveis.includes(novoTipo.trim())) {
      setTiposDisponiveis(prev => [...prev, novoTipo.trim()]);
      setFormData(prev => ({
        ...prev,
        tipos: [...prev.tipos, novoTipo.trim()]
      }));
      setNovoTipo("");
    }
  };

  const handleIniciarEdicaoTipo = (tipo) => {
    setEditingTipo(tipo);
    setEditingTipoValor(tipo);
  };

  const handleSalvarEdicaoTipo = () => {
    const novoTexto = editingTipoValor.trim();
    if (!novoTexto || novoTexto === editingTipo) {
      setEditingTipo(null);
      setEditingTipoValor("");
      return;
    }
    if (tiposDisponiveis.includes(novoTexto)) {
      toast.error("Já existe um tipo com esse nome");
      return;
    }
    setTiposDisponiveis(prev => prev.map(t => t === editingTipo ? novoTexto : t));
    setFormData(prev => ({
      ...prev,
      tipos: prev.tipos.map(t => t === editingTipo ? novoTexto : t)
    }));
    setEditingTipo(null);
    setEditingTipoValor("");
    toast.success("Tipo alterado");
  };

  const handleRemoverTipo = (tipo) => {
    setTiposDisponiveis(prev => prev.filter(t => t !== tipo));
    setFormData(prev => ({
      ...prev,
      tipos: prev.tipos.filter(t => t !== tipo)
    }));
    setEditingTipo(null);
    setEditingTipoValor("");
    toast.success("Tipo removido");
  };

  const handleAdicionarCompetencia = () => {
    if (novaCompetencia.trim() && !formData.competencias_tecnicas.includes(novaCompetencia.trim())) {
      setFormData(prev => ({
        ...prev,
        competencias_tecnicas: [...prev.competencias_tecnicas, novaCompetencia.trim()]
      }));
      setNovaCompetencia("");
    }
  };

  const handleRemoverCompetencia = (comp) => {
    setFormData(prev => ({
      ...prev,
      competencias_tecnicas: prev.competencias_tecnicas.filter(c => c !== comp)
    }));
  };

  const handleUploadAnexoContrato = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!novoAnexoDescricao.trim()) {
      toast.error("Adicione uma descrição para o anexo");
      return;
    }

    setUploadingAnexoContrato(true);
    try {
      const { file_url } = await uploadFile(file);
      
      const novoAnexo = {
        descricao: novoAnexoDescricao.trim(),
        anexo_url: file_url,
        anexo_nome: file.name
      };

      setFormData(prev => ({
        ...prev,
        anexos_contrato: [...prev.anexos_contrato, novoAnexo]
      }));

      setNovoAnexoDescricao("");
      e.target.value = '';
      toast.success("Anexo adicionado com sucesso!");
    } catch (error) {
      console.error("Erro ao fazer upload:", error);
      toast.error("Erro ao fazer upload do anexo");
    } finally {
      setUploadingAnexoContrato(false);
    }
  };

  const handleRemoverAnexoContrato = (index) => {
    setFormData(prev => ({
      ...prev,
      anexos_contrato: prev.anexos_contrato.filter((_, i) => i !== index)
    }));
    toast.success("Anexo removido");
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Preparar dados para envio
    const dataToSave = {
      ...formData,
      valor_contrato: formData.valor_contrato ? parseFloat(formData.valor_contrato) : undefined
    };
    
    // Remover campos vazios
    Object.keys(dataToSave).forEach(key => {
      if (dataToSave[key] === "" || dataToSave[key] === null) {
        delete dataToSave[key];
      }
    });
    
    saveMutation.mutate(dataToSave);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-4">
        <div>
          <Label>Upload do Atestado (PDF)</Label>
          <div className="mt-2 border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors cursor-pointer">
            <input
              type="file"
              accept=".pdf"
              onChange={handleUploadAnexo}
              disabled={uploading || processando}
              className="hidden"
              id="upload-atestado"
            />
            <label htmlFor="upload-atestado" className="cursor-pointer">
              {uploading || processando ? (
                <div className="flex flex-col items-center gap-3">
                  <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
                  <p className="text-sm text-slate-600">
                    {processando ? "Processando atestado..." : "Fazendo upload..."}
                  </p>
                  <div className="w-full max-w-xs space-y-1">
                    <div className="h-2 w-full overflow-hidden rounded-full bg-slate-200">
                      <div className="h-full w-1/3 bg-blue-600 rounded-full animate-progress-loading" />
                    </div>
                    <div className="flex items-center justify-center gap-1 text-slate-500 text-xs">
                      <Clock className="w-3 h-3" />
                      <span className="font-mono">{formatarTempo(tempoDecorrido)}</span>
                    </div>
                  </div>
                </div>
              ) : formData.anexo_nome ? (
                <div className="flex flex-col items-center">
                  <Upload className="w-8 h-8 text-green-600 mb-2" />
                  <p className="text-sm font-medium text-slate-700">{formData.anexo_nome}</p>
                  <p className="text-xs text-slate-500 mt-1">Clique para substituir</p>
                </div>
              ) : (
                <div className="flex flex-col items-center">
                  <Upload className="w-8 h-8 text-slate-400 mb-2" />
                  <p className="text-sm font-medium text-slate-700">
                    Clique para fazer upload do atestado
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    A IA extrairá automaticamente as informações
                  </p>
                </div>
              )}
            </label>
          </div>
        </div>

        <div>
          <Label htmlFor="instituicao">Instituição *</Label>
          <Input
            id="instituicao"
            value={formData.instituicao}
            onChange={(e) => setFormData({ ...formData, instituicao: e.target.value })}
            placeholder="Nome da instituição emissora"
            required
          />
        </div>

        <div>
          <Label>Tipos de Serviço</Label>
          <p className="text-sm text-slate-500 mb-2">Clique no tipo para marcar/desmarcar. Use o lápis para editar o nome ou o X para remover da lista.</p>
          <div className="flex flex-wrap gap-2 mt-2 mb-2">
            {tiposDisponiveis.map(tipo => (
              editingTipo === tipo ? (
                <div key={tipo} className="flex items-center gap-1">
                  <Input
                    value={editingTipoValor}
                    onChange={(e) => setEditingTipoValor(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleSalvarEdicaoTipo();
                      if (e.key === "Escape") { setEditingTipo(null); setEditingTipoValor(""); }
                    }}
                    className="h-8 w-40"
                    autoFocus
                  />
                  <Button type="button" size="sm" variant="default" onClick={handleSalvarEdicaoTipo}>OK</Button>
                  <Button type="button" size="sm" variant="ghost" onClick={() => { setEditingTipo(null); setEditingTipoValor(""); }}><X className="w-4 h-4" /></Button>
                </div>
              ) : (
                <Badge
                  key={tipo}
                  variant={formData.tipos.includes(tipo) ? "default" : "outline"}
                  className={`cursor-pointer flex items-center gap-1 ${
                    formData.tipos.includes(tipo)
                      ? 'bg-blue-600 hover:bg-blue-700'
                      : 'hover:bg-slate-100'
                  }`}
                  onClick={() => handleToggleTipo(tipo)}
                >
                  {tipo}
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); handleIniciarEdicaoTipo(tipo); }}
                    className="hover:bg-white/20 rounded p-0.5"
                    title="Editar nome do tipo"
                  >
                    <Pencil className="w-3 h-3" />
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); handleRemoverTipo(tipo); }}
                    className="hover:bg-red-500/30 rounded p-0.5"
                    title="Remover tipo da lista"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              )
            ))}
          </div>
          <div className="flex gap-2">
            <Input
              placeholder="Adicionar novo tipo"
              value={novoTipo}
              onChange={(e) => setNovoTipo(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAdicionarTipo())}
            />
            <Button type="button" onClick={handleAdicionarTipo} variant="outline">
              <Plus className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <div>
          <Label htmlFor="resumo">Resumo Executivo</Label>
          <Textarea
            id="resumo"
            value={formData.resumo_executivo}
            onChange={(e) => setFormData({ ...formData, resumo_executivo: e.target.value })}
            placeholder="Descreva o escopo e principais características do projeto..."
            rows={4}
          />
        </div>

        <div>
          <Label>Competências Técnicas</Label>
          <div className="flex flex-wrap gap-2 mt-2 mb-2">
            {formData.competencias_tecnicas.map(comp => (
              <Badge key={comp} variant="outline" className="bg-green-50 text-green-700">
                {comp}
                <button
                  type="button"
                  onClick={() => handleRemoverCompetencia(comp)}
                  className="ml-1 hover:text-red-600"
                >
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            ))}
          </div>
          <div className="flex gap-2">
            <Input
              placeholder="Ex: Java, PHP, NodeJS, React..."
              value={novaCompetencia}
              onChange={(e) => setNovaCompetencia(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAdicionarCompetencia())}
            />
            <Button type="button" onClick={handleAdicionarCompetencia} variant="outline">
              <Plus className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="data_emissao">Data de Emissão</Label>
            <Input
              id="data_emissao"
              type="date"
              value={formData.data_emissao}
              onChange={(e) => setFormData({ ...formData, data_emissao: e.target.value })}
            />
          </div>

          <div>
            <Label htmlFor="valor_contrato">Valor do Contrato</Label>
            <Input
              id="valor_contrato"
              type="number"
              step="0.01"
              value={formData.valor_contrato}
              onChange={(e) => setFormData({ ...formData, valor_contrato: e.target.value })}
              placeholder="0.00"
            />
          </div>

          <div>
            <Label htmlFor="data_inicio">Data de Início</Label>
            <Input
              id="data_inicio"
              type="date"
              value={formData.data_inicio}
              onChange={(e) => setFormData({ ...formData, data_inicio: e.target.value })}
            />
          </div>

          <div>
            <Label htmlFor="data_fim">Data de Término</Label>
            <Input
              id="data_fim"
              type="date"
              value={formData.data_fim}
              onChange={(e) => setFormData({ ...formData, data_fim: e.target.value })}
            />
          </div>
        </div>

        <div>
          <Label htmlFor="observacoes">Observações</Label>
          <Textarea
            id="observacoes"
            value={formData.observacoes}
            onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
            placeholder="Informações adicionais..."
            rows={3}
          />
        </div>

        <div className="border-t pt-4">
          <Label>Anexo "Atestado de Capacidade Técnica"</Label>
          <p className="text-sm text-slate-500 mb-3">Adicione documentos relacionados a "Atestado de Capacidade Técnica"</p>
          
          {/* Lista de anexos existentes */}
          {formData.anexos_contrato.length > 0 && (
            <div className="space-y-2 mb-4">
              {formData.anexos_contrato.map((anexo, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-900">{anexo.descricao}</p>
                    <p className="text-xs text-slate-500 mt-1">{anexo.anexo_nome}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => openUploadInNewTab(anexo.anexo_url)}
                    >
                      Ver
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoverAnexoContrato(index)}
                      className="hover:bg-red-100 hover:text-red-600"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Formulário para adicionar novo anexo */}
          <div className="space-y-3 p-4 border-2 border-dashed border-slate-300 rounded-lg">
            <div>
              <Label htmlFor="descricao_anexo">Descrição do Anexo</Label>
              <Input
                id="descricao_anexo"
                value={novoAnexoDescricao}
                onChange={(e) => setNovoAnexoDescricao(e.target.value)}
                placeholder="Ex.: Atestado de capacidade técnica"
              />
            </div>
            
            <div>
              <input
                type="file"
                onChange={handleUploadAnexoContrato}
                disabled={uploadingAnexoContrato || !novoAnexoDescricao.trim()}
                className="hidden"
                id="upload-anexo-contrato"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => document.getElementById('upload-anexo-contrato')?.click()}
                disabled={uploadingAnexoContrato || !novoAnexoDescricao.trim()}
                className="w-full"
              >
                {uploadingAnexoContrato ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Adicionar Arquivo
                  </>
                )}
              </Button>
              {!novoAnexoDescricao.trim() && (
                <p className="text-xs text-amber-600 mt-1">Digite uma descrição antes de fazer upload</p>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t">
        <Button type="button" variant="outline" onClick={onClose}>
          Cancelar
        </Button>
        <Button 
          type="submit" 
          disabled={saveMutation.isPending}
          className="bg-blue-600 hover:bg-blue-700"
        >
          {saveMutation.isPending ? 'Salvando...' : (atestado ? 'Atualizar' : 'Criar')} Atestado
        </Button>
      </div>
    </form>
  );
}